import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from 'vue'
  import { useRouter } from 'vue-router/composables'

  import GetStartedPanelLoader from '@/components/loaders/GetStartedPanelLoader.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import ContactMe from '@/components/common/ContactMe.vue'

  import useIcons from '@/composables/useIcons'
  import useI18n from '@/composables/useI18n'
  import useVideo from '@/composables/useVideo'
  import useCourse from '@/composables/useCourse'

  import { UserModule } from '@/store/modules/user'
  import useCommonMixin from '@/composables/useCommonMixin'

  interface Step {
    active: boolean
    completed: boolean
    requiresUser?: boolean
    action: () => void
    getTitle: () => string
  }

  
export default /*#__PURE__*/_defineComponent({
  __name: 'GetStartedPanel',
  setup(__props) {

  const router = useRouter()

  const { checkboxSquare, checkedCircle, michaelPortrait, play, expandIcon, collapseIcon } = useIcons()
  const { translateString } = useI18n()
  const { getPosterImage } = useVideo()
  const { hasValidUser, showCompanyOnboardingPopupDetails } = useCommonMixin()
  const { course, nextModuleToStart, navigateToCourseContent, navigateToModule, showStartCourseForFreeModal } =
    useCourse()

  const isCollapsed = ref(false)

  const loading = computed(() => UserModule.fetchingOnboardingPopupDetails || !course.value)

  const companyOnboardingPopupDetails = computed(() => UserModule.companyOnboardingPopupDetails)

  const introVideoWatched = computed(() => UserModule.getStartedChecklist?.introVideoWatched ?? false)

  const steps = computed<Step[]>(() => {
    const baseSteps: Step[] = [
      {
        active: false,
        completed: UserModule.getStartedChecklist?.accountCreated ?? false,
        requiresUser: false,
        action: () => router.push('/login'),
        getTitle: () => translateString('homepage.getStartedChecklist.createYourAccount'),
      },
    ]

    if (companyOnboardingPopupDetails.value) {
      baseSteps.push({
        active: false,
        completed: introVideoWatched.value,
        requiresUser: true,
        action: () => showCompanyOnboardingPopupDetails(),
        getTitle: () => translateString('homepage.getStartedChecklist.introVideoWatched'),
      })
    }

    baseSteps.push(
      {
        active: false,
        completed:
          (UserModule.getStartedChecklist?.read5Lessons ?? false) || (course.value?.numberOfLessonsCompleted ?? 0) >= 5,
        requiresUser: true,
        action: () => handleRouteToNextLesson(),
        getTitle: () => {
          const lessonsCompleted = course.value?.numberOfLessonsCompleted ?? 0
          if (lessonsCompleted >= 5) {
            return translateString('homepage.getStartedChecklist.read5Lessons')
          }
          if (lessonsCompleted === 4) {
            return translateString('homepage.getStartedChecklist.readOneMoreLesson')
          }
          if (lessonsCompleted === 3) {
            return translateString('homepage.getStartedChecklist.read2MoreLessons')
          }
          if (lessonsCompleted === 2) {
            return translateString('homepage.getStartedChecklist.read3MoreLessons')
          }
          if (lessonsCompleted === 1) {
            return translateString('homepage.getStartedChecklist.read4MoreLessons')
          }
          return translateString('homepage.getStartedChecklist.read5Lessons')
        },
      },
      {
        active: false,
        completed: UserModule.getStartedChecklist?.bookmarkLesson ?? false,
        requiresUser: true,
        action: () => handleRouteToNextLesson(true),
        getTitle: () => translateString('homepage.getStartedChecklist.bookmarkALesson'),
      },
    )

    if (course.value?.exam) {
      baseSteps.push({
        active: false,
        completed: UserModule.getStartedChecklist?.startExamSimulation ?? false,
        requiresUser: true,
        action: () => router.push(`/courses/${course.value!.id}/exam/${course.value!.exam!.id}/start`),
        getTitle: () => translateString('homepage.getStartedChecklist.startAnExam'),
      })
    }

    return baseSteps
  })

  const allStepsCompleted = computed(() => steps.value.every((step) => step.completed))

  const nextIncompleteStepIndex = computed(() => steps.value.findIndex((step) => !step.completed))

  const popupPosterImage = computed(() => {
    if (!companyOnboardingPopupDetails.value) return ''

    return (
      companyOnboardingPopupDetails.value.posterImageUrl ??
      getPosterImage(
        companyOnboardingPopupDetails.value.videoUrl ?? '',
        companyOnboardingPopupDetails.value.timestamp ?? 0,
      )
    )
  })

  const nextLesson = computed(() => {
    if (!nextModuleToStart.value) return null

    return nextModuleToStart.value.lessons.find((lesson) => !lesson.completed) || nextModuleToStart.value.lessons[0]
  })

  const updateActiveStep = () => {
    steps.value.forEach((step, index) => {
      step.active = index === nextIncompleteStepIndex.value
    })
  }

  const handleStepClick = (step: Step) => {
    if (step.requiresUser && !hasValidUser.value) {
      router.push('/login')
      return
    }

    if (step.completed) return

    if (step.action) {
      step.action()
    }
  }

  const handleRouteToNextLesson = (shouldScrollToBookmark?: boolean) => {
    if (course.value?.purchased || course.value?.expired) {
      navigateToCourseContent(nextModuleToStart.value, nextLesson.value, course.value!.id, false)
    } else if (course.value?.trial) {
      navigateToModule(course.value!.id, nextModuleToStart.value, nextLesson.value, shouldScrollToBookmark)
    } else {
      showStartCourseForFreeModal({
        scrollToBookmark: shouldScrollToBookmark,
      })
    }
  }

  const toggleCollapse = (event: MouseEvent) => {
    if (isCollapsed.value) {
      isCollapsed.value = false
    } else {
      event.stopPropagation()
      isCollapsed.value = true
    }
  }

  watch(nextIncompleteStepIndex, updateActiveStep, { immediate: true })
  watch(steps, updateActiveStep, { immediate: true })
  watch(
    allStepsCompleted,
    (completed) => {
      if (completed) {
        isCollapsed.value = true
      }
    },
    { immediate: true },
  )

return { __sfc: true,router, checkboxSquare, checkedCircle, michaelPortrait, play, expandIcon, collapseIcon, translateString, getPosterImage, hasValidUser, showCompanyOnboardingPopupDetails, course, nextModuleToStart, navigateToCourseContent, navigateToModule, showStartCourseForFreeModal, isCollapsed, loading, companyOnboardingPopupDetails, introVideoWatched, steps, allStepsCompleted, nextIncompleteStepIndex, popupPosterImage, nextLesson, updateActiveStep, handleStepClick, handleRouteToNextLesson, toggleCollapse, ref, computed, watch, useRouter, GetStartedPanelLoader, SVGRenderer, ContactMe, useIcons, useI18n, useVideo, useCourse, UserModule, useCommonMixin }
}

})